import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import type { AffiliateUserProfile } from '@/types/affiliate-user';
import type { ApiResponse } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import type { AxiosError } from 'axios';

export type AffiliateActivationAction = 'activate' | 'deactivate';

export interface AffiliateActivationRequest {
  action: AffiliateActivationAction;
}

const performActivationAction = async (
  userId: string,
  programId: string,
  action: AffiliateActivationAction
): Promise<ApiResponse> => {
  const endpoint =
    action === 'activate'
      ? `/dashboard/affiliates/${userId}/programs/${programId}/activate`
      : `/dashboard/affiliates/${userId}/programs/${programId}/deactivate`;

  const response = await api.put<ApiResponse>(endpoint);

  if (!response.data.success) {
    throw new Error(response.data.message || `Failed to ${action} affiliate`);
  }

  return response.data;
};

export const useAffiliateActivationAction = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse,
    Error | AxiosError<ApiResponse>,
    { userId: string; programId: string; action: AffiliateActivationAction },
    { previousAffiliateUser?: AffiliateUserProfile | null }
  >({
    mutationFn: ({ userId, programId, action }) =>
      performActivationAction(userId, programId, action),
    onMutate: async (variables) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: ['affiliateUser', variables.userId],
      });

      // Snapshot the previous value for rollback
      const previousAffiliateUser = queryClient.getQueryData<AffiliateUserProfile>([
        'affiliateUser',
        variables.userId,
      ]);

      // Optimistically update the affiliate user data
      if (previousAffiliateUser) {
        queryClient.setQueryData<AffiliateUserProfile>(
          ['affiliateUser', variables.userId],
          (old: AffiliateUserProfile | undefined) => {
            if (!old) return old;

            return {
              ...old,
              programs: old.programs.map((program) =>
                program.id === variables.programId
                  ? {
                      ...program,
                      // Update both fields for compatibility
                      isActive: variables.action === 'activate',
                      deactivatedAt:
                        variables.action === 'deactivate' ? new Date().toISOString() : null,
                    }
                  : program
              ),
            };
          }
        );
      }

      // Return context object with the snapshot value
      return { previousAffiliateUser };
    },
    onError: (error, variables, context) => {
      // Rollback to the previous state
      if (context?.previousAffiliateUser) {
        queryClient.setQueryData(
          ['affiliateUser', variables.userId],
          context.previousAffiliateUser
        );
      }

      console.error(`Failed to ${variables.action} affiliate:`, error);
      const errorMessage =
        ('response' in error ? error.response?.data?.message : undefined) || error.message;
      const titleKey =
        variables.action === 'activate'
          ? 'affiliates.activation.activateFailed'
          : 'affiliates.activation.deactivateFailed';
      const descKey =
        variables.action === 'activate'
          ? 'affiliates.activation.activateFailedDesc'
          : 'affiliates.activation.deactivateFailedDesc';

      toaster.error({
        title: i18next.t(titleKey),
        description: errorMessage || i18next.t(descKey),
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch queries to ensure data consistency
      queryClient.invalidateQueries({
        queryKey: ['affiliateUser', variables.userId],
      });

      queryClient.invalidateQueries({
        queryKey: ['affiliate-details', variables.userId, variables.programId],
      });

      void queryClient.invalidateQueries({ queryKey: ['contact-affiliation'] });

      // Invalidate affiliate searches that might include this user
      queryClient.invalidateQueries({
        queryKey: ['affiliates', 'search'],
      });

      // Invalidate affiliate list queries
      queryClient.invalidateQueries({
        queryKey: ['affiliates'],
      });

      const titleKey =
        variables.action === 'activate'
          ? 'affiliates.activation.activated'
          : 'affiliates.activation.deactivated';
      const descKey =
        variables.action === 'activate'
          ? 'affiliates.activation.activatedDesc'
          : 'affiliates.activation.deactivatedDesc';

      toaster.success({
        title: i18next.t(titleKey),
        description: i18next.t(descKey),
      });
    },
  });
};
